import { useState } from 'react';
import { Search, Plus, LogIn, LogOut, Menu, Shield } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import logo from '@/assets/trustroi-logo.png';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';

const navLinks = [
  { label: 'Leaderboard', href: '/' },
  { label: 'TrustWall — Verified Testimonials', href: '/trustwall' },
  { label: 'Stack Builder', href: '/stack-builder' },
  { label: 'Categories', href: '/categories' },
  { label: 'How ROI Works', href: '/how-it-works' },
  { label: 'Alternatives', href: '/alternatives' },
  { label: 'Blog', href: '/blog' },
  { label: 'Sponsorship', href: '/sponsorship' },
];

export function Header() {
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdmin();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={logo} alt="TrustROI logo" width="32" height="32" fetchPriority="high" decoding="async" className="h-7 w-7 sm:h-8 sm:w-8" />
            <span className="font-display text-lg sm:text-xl font-bold text-foreground">
              TrustROI
            </span>
          </Link>

          {/* Spacer */}
          <div className="flex-1 hidden md:block" />

          {/* Actions */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {isAdmin && (
              <Link to="/add#sponsorships" className="hidden sm:block">
                <Button size="sm" variant="outline" className="gap-1.5 font-semibold text-primary border-primary/30">
                  <Shield className="h-4 w-4" />
                  Admin Panel
                </Button>
              </Link>
            )}
            <Link to="/add" className="hidden sm:block">
              <Button size="sm" className="gap-1.5 font-semibold">
                <Plus className="h-4 w-4" />
                Add your tool
              </Button>
            </Link>

            {user ? (
              <Button size="sm" variant="ghost" onClick={signOut} className="gap-1.5 hidden sm:inline-flex">
                <LogOut className="h-4 w-4" />
                <span className="hidden lg:inline">Sign Out</span>
              </Button>
            ) : (
              <Link to="/login" className="hidden sm:block">
                <Button size="sm" variant="ghost" className="gap-1.5">
                  <LogIn className="h-4 w-4" />
                  <span className="hidden lg:inline">Sign In</span>
                </Button>
              </Link>
            )}

            {/* Mobile hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="sm:hidden">
                <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Open navigation menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[260px] p-0">
                <SheetHeader className="p-4 border-b border-border">
                  <SheetTitle className="text-left font-display">Menu</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col p-4 gap-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.label}
                      to={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="px-3 py-2.5 text-sm text-foreground hover:bg-secondary rounded-md transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="border-t border-border my-2" />
                  {isAdmin && (
                    <Link to="/add#sponsorships" onClick={() => setMobileOpen(false)}>
                      <Button size="sm" variant="outline" className="w-full gap-1.5 font-semibold text-primary border-primary/30">
                        <Shield className="h-4 w-4" />
                        Admin Panel
                      </Button>
                    </Link>
                  )}
                  <Link to="/add" onClick={() => setMobileOpen(false)}>
                    <Button size="sm" className="w-full gap-1.5 font-semibold">
                      <Plus className="h-4 w-4" />
                      Add your tool
                    </Button>
                  </Link>
                  {user ? (
                    <Button size="sm" variant="ghost" onClick={() => { signOut(); setMobileOpen(false); }} className="w-full gap-1.5 mt-1">
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </Button>
                  ) : (
                    <Link to="/login" onClick={() => setMobileOpen(false)}>
                      <Button size="sm" variant="ghost" className="w-full gap-1.5 mt-1">
                        <LogIn className="h-4 w-4" />
                        Sign In
                      </Button>
                    </Link>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Desktop nav */}
        <nav className="hidden sm:flex items-center gap-1 mt-3 overflow-x-auto scrollbar-none">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap rounded-md hover:bg-secondary"
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
}
